import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import {
  PlatformGlyph,
  IconKey,
  IconCheck,
  IconTrash,
  IconExternal,
  IconShield,
  IconRefresh,
} from '../lib/icons';

const SOURCE_LABEL = {
  stored: 'salvo no painel',
  env: 'vem do .env',
  none: 'não definido',
};

const SOURCE_CLS = {
  stored: 'ok',
  env: 'info',
  none: 'muted',
};

export default function Credentials({ publications, onRefresh }) {
  const [configs, setConfigs] = useState([]);
  const [draft, setDraft] = useState({});
  const [webhook, setWebhook] = useState('');
  const [method, setMethod] = useState('auto');
  const [metrics, setMetrics] = useState({ autoMetrics: true, autoMetricsIntervalMin: 30, autoMetricsMaxAgeDays: 30 });
  const [collecting, setCollecting] = useState('');
  const [lastRun, setLastRun] = useState(null);
  const [hook, setHook] = useState(null);
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState(null);
  const mounted = useRef(true);
  const msgTimer = useRef(null);

  const notify = (type, text) => {
    setMsg({ type, text });
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => {
      if (mounted.current) setMsg(null);
    }, 7000);
  };

  const load = useCallback(async () => {
    try {
      const [keys, settings, hookStatus] = await Promise.all([
        api.get('/keys'),
        api.get('/settings'),
        api.get('/webhooks/status'),
      ]);
      if (!mounted.current) return;
      setConfigs(keys);
      setHook(hookStatus);
      setWebhook(settings.webhookUrl || '');
      setMethod(settings.publishMethod || 'auto');
      setMetrics({
        autoMetrics: settings.autoMetrics !== false,
        autoMetricsIntervalMin: Number(settings.autoMetricsIntervalMin) || 30,
        autoMetricsMaxAgeDays: Number(settings.autoMetricsMaxAgeDays) || 30,
      });
    } catch (err) {
      if (mounted.current) notify('err', err.message);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      if (msgTimer.current) clearTimeout(msgTimer.current);
    };
  }, [load]);

  const setField = (platformId, key, value) => {
    setDraft((prev) => ({ ...prev, [`${platformId}:${key}`]: value }));
  };

  const savePlatform = async (platformId) => {
    const platform = configs.find((p) => p.id === platformId);
    if (!platform) return;
    const values = {};
    platform.required.forEach((field) => {
      const value = (draft[`${platformId}:${field.key}`] || '').trim();
      if (value) values[field.key] = value;
    });
    if (Object.keys(values).length === 0) {
      notify('info', 'Nenhum valor digitado para gravar.');
      return;
    }
    setSaving(platformId);
    try {
      const updated = await api.put(`/keys/${platformId}`, { values });
      setConfigs((prev) => prev.map((p) => (p.id === platformId ? updated : p)));
      setDraft((prev) => {
        const next = { ...prev };
        platform.required.forEach((field) => delete next[`${platformId}:${field.key}`]);
        return next;
      });
      onRefresh();
      notify(
        'ok',
        updated.mode === 'live'
          ? `${updated.name} passou para o modo AO VIVO.`
          : `${updated.name} salvo — ainda faltam chaves para sair do modo demo.`
      );
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const clearPlatform = async (platformId) => {
    const platform = configs.find((p) => p.id === platformId);
    if (!platform) return;
    const confirmed = window.confirm(
      `Remover as chaves salvas de ${platform.name}? Você precisará configurá-las novamente para publicar.`
    );
    if (!confirmed) return;
    setSaving(platformId);
    try {
      const updated = await api.del(`/keys/${platformId}`);
      setConfigs((prev) => prev.map((p) => (p.id === platformId ? updated : p)));
      onRefresh();
      notify('ok', `Chaves de ${platform.name} removidas do painel.`);
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const saveWebhook = async () => {
    setSaving('webhook');
    try {
      const settings = await api.put('/settings', { webhookUrl: webhook.trim() });
      setWebhook(settings.webhookUrl || '');
      notify('ok', 'Webhook de ferramentas de marketing salvo.');
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const refreshHook = async () => {
    try {
      setHook(await api.get('/webhooks/status'));
    } catch (err) {
      /* mantém o último status */
    }
  };

  const showSecret = async (rotate) => {
    setSaving(rotate ? 'rotate' : 'secret');
    try {
      const data = rotate ? await api.post('/webhooks/secret', {}) : await api.get('/webhooks/secret');
      let copied = false;
      try {
        await navigator.clipboard.writeText(data.secret);
        copied = true;
      } catch (err) {
        copied = false;
      }
      window.prompt(
        copied ? 'Segredo copiado para a área de transferência:' : 'Segredo de assinatura (HMAC):',
        data.secret
      );
      notify('ok', rotate ? 'Segredo novo gerado — atualize a assinatura no destino.' : 'Segredo copiado.');
      await refreshHook();
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const testWebhook = async () => {
    setSaving('test');
    try {
      await api.post('/webhooks/test', { url: webhook.trim() });
      notify('ok', 'Evento de teste entregue com assinatura.');
      await refreshHook();
    } catch (err) {
      notify('err', err.message);
      await refreshHook();
    } finally {
      setSaving('');
    }
  };

  const retryDelivery = async (id) => {
    setSaving(`retry-${id}`);
    try {
      await api.post(`/webhooks/deliveries/${id}/retry`, {});
      notify('ok', 'Entrega reenviada com sucesso.');
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
      await refreshHook();
    }
  };

  const saveMethod = async (value) => {
    setSaving('method');
    try {
      const settings = await api.put('/settings', { publishMethod: value });
      setMethod(settings.publishMethod || 'auto');
      onRefresh();
      notify(
        'ok',
        value === 'manual'
          ? 'Modo MANUAL ativado — você abre o site de cada plataforma, publica lá e confirma aqui.'
          : 'Modo automático ativado (usa chaves de API; sem chave, vira demo).'
      );
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const saveMetrics = async () => {
    setSaving('metrics');
    try {
      const settings = await api.put('/settings', metrics);
      setMetrics({
        autoMetrics: settings.autoMetrics !== false,
        autoMetricsIntervalMin: Number(settings.autoMetricsIntervalMin) || 30,
        autoMetricsMaxAgeDays: Number(settings.autoMetricsMaxAgeDays) || 30,
      });
      notify('ok', 'Configuração das métricas automáticas salva.');
    } catch (err) {
      notify('err', err.message);
    } finally {
      setSaving('');
    }
  };

  const collectNow = async () => {
    setCollecting('run');
    try {
      const summary = await api.post('/metrics/run', {});
      setLastRun(summary);
      onRefresh();
      notify(
        'ok',
        summary.disabled
          ? 'Coleta automática está desativada — ative acima para rodar sozinha.'
          : `Coleta: ${summary.updated} atualizada(s), ${summary.skipped} sem dado real, ${summary.scanned} analisada(s).`
      );
    } catch (err) {
      notify('err', err.message);
    } finally {
      setCollecting('');
    }
  };

  const lastCollected = (publications || [])
    .map((p) => p.metricsCollectedAt)
    .filter(Boolean)
    .sort()
    .pop();

  const live = configs.filter((c) => c.mode === 'live').length;
  const configuredFields = configs.reduce(
    (acc, c) => acc + c.required.filter((r) => r.set).length,
    0
  );
  const totalFields = configs.reduce((acc, c) => acc + c.required.length, 0);

  return (
    <div>
      {msg ? <div className={`banner ${msg.type}`}>{msg.text}</div> : null}

      <div className="cards">
        <div className="card">
          <div className="k">Plataformas ao vivo</div>
          <div className="v">
            {live}/{configs.length}
          </div>
          <div className="s">publicam nas APIs reais</div>
        </div>
        <div className="card">
          <div className="k">Chaves configuradas</div>
          <div className="v">
            {configuredFields}/{totalFields}
          </div>
          <div className="s">credenciais obrigatórias</div>
        </div>
        <div className="card">
          <div className="k">Modo atual</div>
          <div className="v" style={{ fontSize: 20 }}>
            {live === 0 ? 'DEMO' : live === configs.length ? 'TOTAL' : 'MISTO'}
          </div>
          <div className="s">{live === 0 ? 'simulação de publicação' : 'envio real + demo'}</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="k" style={{ marginBottom: 8 }}>
          Método de publicação
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`btn ${method === 'manual' ? 'primary' : 'ghost'} small`}
            disabled={saving === 'method'}
            onClick={() => saveMethod('manual')}
          >
            Manual assistido
          </button>
          <button
            type="button"
            className={`btn ${method === 'auto' ? 'primary' : 'ghost'} small`}
            disabled={saving === 'method'}
            onClick={() => saveMethod('auto')}
          >
            Automático (API)
          </button>
        </div>
        <div className="s" style={{ marginTop: 8 }}>
          {method === 'manual'
            ? 'Você abre o site de cada plataforma (conta já logada no navegador), publica lá e confirma aqui. Nenhuma chave de API é usada.'
            : 'Usa as chaves de API; plataformas sem chave ficam em modo demo (simulação).'}
        </div>
      </div>

      <div className="banner info">
        As credenciais são <strong>criptografadas (AES-256-GCM)</strong> e guardadas em
        <code> backend/data</code>. Valores secretos aparecem mascarados — digite um novo valor para substituir.
      </div>

      <div className="grid">
        {configs.map((platform) => (
          <div className="plat-card" key={platform.id}>
            <div className="plat-head">
              <div className="plat-logo" style={{ background: platform.color }}>
                <PlatformGlyph id={platform.id} size={22} />
              </div>
              <div>
                <div className="plat-name">{platform.name}</div>
                <div className="plat-sub">{platform.hint}</div>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <span className={`badge ${platform.mode === 'live' ? 'ok' : 'muted'}`}>
                  {platform.mode === 'live' ? 'AO VIVO' : 'DEMO'}
                </span>
              </div>
            </div>

            <div className="connect-form" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
              {platform.required.map((field) => (
                <div className="field" key={field.key}>
                  <label>
                    {field.key}{' '}
                    <span className={`badge ${SOURCE_CLS[field.source]}`} style={{ marginLeft: 4 }}>
                      {SOURCE_LABEL[field.source]}
                    </span>
                  </label>
                  <input
                    className="input"
                    type={field.secret ? 'password' : 'text'}
                    value={draft[`${platform.id}:${field.key}`] || ''}
                    onChange={(e) => setField(platform.id, field.key, e.target.value)}
                    placeholder={field.value || 'não definido'}
                    autoComplete="off"
                  />
                </div>
              ))}
            </div>

            <div className="plat-actions">
              <button
                type="button"
                className="btn primary small"
                disabled={saving === platform.id}
                onClick={() => savePlatform(platform.id)}
              >
                <span className="btn-ico">
                  <IconCheck size={14} />
                </span>
                {saving === platform.id ? 'Salvando…' : 'Salvar chaves'}
              </button>
              <button
                type="button"
                className="btn danger small"
                disabled={saving === platform.id}
                onClick={() => clearPlatform(platform.id)}
              >
                <span className="btn-ico">
                  <IconTrash size={14} />
                </span>
                Limpar
              </button>
              <a className="btn ghost small" href={platform.docs} target="_blank" rel="noopener noreferrer">
                <span className="btn-ico">
                  <IconExternal size={14} />
                </span>
                Documentação
              </a>
            </div>
          </div>
        ))}
      </div>

      <section className="section" style={{ marginTop: 18 }}>
        <div className="section-head">
          <h2>
            <span className="head-ico">
              <IconShield size={16} />
            </span>
            Ferramentas externas (webhook)
          </h2>
          {hook ? (
            <span className={`badge ${hook.url ? 'ok' : 'muted'}`}>{hook.url ? 'configurado' : 'sem URL'}</span>
          ) : null}
        </div>
        <div className="hint" style={{ marginBottom: 12 }}>
          A cada publicação concluída o sistema envia <code>POST</code> com{' '}
          <code>{'{ event, publication }'}</code> e reenvia automaticamente em falha (até {hook ? hook.maxAttempts : 5}{' '}
          tentativas). Confira a assinatura nos headers <code>X-MH-Signature</code>, <code>X-MH-Timestamp</code>,{' '}
          <code>X-MH-Event</code> e <code>X-MH-Delivery</code>.
        </div>
        <div className="field">
          <label>URL do webhook</label>
          <input
            className="input"
            type="url"
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder="https://hooks.exemplo.com/marketing"
            autoComplete="off"
          />
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          <button type="button" className="btn primary small" disabled={saving === 'webhook'} onClick={saveWebhook}>
            <span className="btn-ico">
              <IconCheck size={14} />
            </span>
            {saving === 'webhook' ? 'Salvando…' : 'Salvar webhook'}
          </button>
          <button type="button" className="btn ghost small" disabled={saving !== ''} onClick={() => showSecret(false)}>
            <span className="btn-ico">
              <IconKey size={14} />
            </span>
            {saving === 'secret' ? '…' : 'Copiar segredo'}
          </button>
          <button type="button" className="btn ghost small" disabled={saving !== ''} onClick={() => showSecret(true)}>
            <span className="btn-ico">
              <IconRefresh size={14} />
            </span>
            {saving === 'rotate' ? '…' : 'Regerar segredo'}
          </button>
          <button
            type="button"
            className="btn success small"
            disabled={saving === 'test' || !webhook.trim()}
            onClick={testWebhook}
          >
            <span className="btn-ico">
              <IconExternal size={14} />
            </span>
            {saving === 'test' ? 'Enviando…' : 'Enviar evento de teste'}
          </button>
        </div>

        {hook ? (
          <div className="hint" style={{ marginTop: 10 }}>
            Segredo atual: <code>{hook.secretMasked}</code>
            {hook.stats ? ` · ${hook.stats.sent} entregue(s), ${hook.stats.pending} na fila, ${hook.stats.failed} falha(s)` : ''}
          </div>
        ) : null}

        {hook && hook.deliveries && hook.deliveries.length > 0 ? (
          <div className="list" style={{ marginTop: 12 }}>
            {hook.deliveries.map((item) => (
              <div className="row" key={item.id} style={{ padding: '8px 12px' }}>
                <div style={{ minWidth: 0 }}>
                  <div className="title">
                    {item.event}{' '}
                    <span
                      className={`badge ${item.status === 'sent' ? 'ok' : item.status === 'failed' ? 'err' : 'warn'}`}
                      style={{ marginLeft: 6 }}
                    >
                      {item.status === 'sent' ? 'entregue' : item.status === 'failed' ? 'falhou' : `tentativa ${item.attempt}`}
                    </span>
                  </div>
                  <div className="meta">
                    {item.deliveredAt
                      ? new Date(item.deliveredAt).toLocaleString('pt-BR')
                      : item.lastError || 'aguardando reenvio'}
                  </div>
                </div>
                {item.status !== 'sent' ? (
                  <div className="actions">
                    <button
                      type="button"
                      className="btn ghost small"
                      disabled={saving === `retry-${item.id}`}
                      onClick={() => retryDelivery(item.id)}
                    >
                      {saving === `retry-${item.id}` ? '…' : 'Reenviar'}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="section" style={{ marginTop: 18 }}>
        <div className="section-head">
          <h2>
            <span className="head-ico">
              <IconRefresh size={16} />
            </span>
            Métricas automáticas
          </h2>
          <span className={`badge ${metrics.autoMetrics ? 'ok' : 'muted'}`}>
            {metrics.autoMetrics ? 'ativadas' : 'desativadas'}
          </span>
        </div>
        <div className="hint" style={{ marginBottom: 12 }}>
          O agendador coleta engajamento sozinho, a cada intervalo, nas publicações feitas com{' '}
          <strong>chave de API real</strong>. Publicações demo ou manuais (sem identificador do post) ficam de fora para
          os números não mudarem sem motivo.
        </div>

        <div className="field" style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label>Coletar automaticamente</label>
            <select
              className="select"
              value={metrics.autoMetrics ? 'on' : 'off'}
              onChange={(e) => setMetrics({ ...metrics, autoMetrics: e.target.value === 'on' })}
            >
              <option value="on">Sim, rodar sozinha</option>
              <option value="off">Não, só manualmente</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label>Intervalo (minutos)</label>
            <input
              className="input"
              type="number"
              min="1"
              max="1440"
              value={metrics.autoMetricsIntervalMin}
              onChange={(e) => setMetrics({ ...metrics, autoMetricsIntervalMin: e.target.value })}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label>Idade máxima (dias)</label>
            <input
              className="input"
              type="number"
              min="1"
              max="3650"
              value={metrics.autoMetricsMaxAgeDays}
              onChange={(e) => setMetrics({ ...metrics, autoMetricsMaxAgeDays: e.target.value })}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 9, marginTop: 4, flexWrap: 'wrap' }}>
          <button type="button" className="btn primary small" disabled={saving === 'metrics'} onClick={saveMetrics}>
            {saving === 'metrics' ? 'Salvando…' : 'Salvar configuração'}
          </button>
          <button type="button" className="btn success small" disabled={collecting !== ''} onClick={collectNow}>
            {collecting ? 'Coletando…' : 'Coletar agora'}
          </button>
        </div>

        <div className="hint" style={{ marginTop: 10 }}>
          {lastCollected ? `Última coleta: ${new Date(lastCollected).toLocaleString('pt-BR')}` : 'Nenhuma coleta ainda.'}
          {lastRun ? ` · última execução: ${lastRun.updated} atualizada(s)` : ''}
        </div>
      </section>
    </div>
  );
}
